import {
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type IBubbleReaction,
    Bubble,
    Button
} from "ncore-ui-kit";
import {
    InlineCode,
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Bubble";

const USAGE_CODE = `const [isReacted, setIsReacted] = useState(false);

<Bubble
    onReactionsPress={() => setIsReacted(!isReacted)}
    reactions={[
        {
            count: isReacted ? 3 : 2,
            isOwn: isReacted,
            emoji: "👍"
        }
    ]}
>
    Hi! How can I help today?
</Bubble>

<Bubble
    variant="user"
>
    Could you send the report?
</Bubble>`;

const GROUP_CODE = `<Bubble.Group
    align="end"
>
    <Bubble
        variant="user"
    >
        Could you send the report?
    </Bubble>
    <Bubble
        variant="user"
    >
        Before noon would be great.
    </Bubble>
</Bubble.Group>`;

const STREAM_CODE = `<Bubble
    isStreaming={isStreaming}
    contentType="markdown"
    variant="plain"
>
    {answer}
</Bubble>

<Bubble>
    <Bubble.Typing />
</Bubble>`;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const READ_ONLY_REACTIONS: Array<IBubbleReaction> = [
    {
        emoji: "👍",
        count: 2
    },
    {
        emoji: "🙏",
        count: 1
    }
];

const OWN_REACTIONS: Array<IBubbleReaction> = [
    {
        emoji: "👍",
        isOwn: true,
        count: 2
    },
    {
        emoji: "❤️",
        count: 1
    }
];

const MANY_REACTIONS: Array<IBubbleReaction> = [
    {
        emoji: "👍",
        count: 3
    },
    {
        emoji: "🙏",
        count: 1
    },
    {
        emoji: "❤️",
        count: 1
    },
    {
        emoji: "🎉",
        count: 1
    }
];

const TOP_REACTIONS: Array<IBubbleReaction> = [
    {
        emoji: "👍",
        count: 1
    }
];

const TYPING_DELAY = 900;

const WORD_DELAY = 110;

const STREAM_DONE = 1000;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.variants"),
        id: "variants"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("bubblePage.reactionsTitle"),
        id: "reactions"
    },
    {
        title: t("bubblePage.contentTitle"),
        id: "content"
    },
    {
        title: t("bubblePage.interactionTitle"),
        id: "interaction"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const ReactionAddDemo = () => {
    const {
        t
    } = useDocsI18n();

    const [
        reactions,
        setReactions
    ] = useState<Array<IBubbleReaction>>([
        {
            emoji: "👍",
            count: 3
        },
        {
            emoji: "🎉",
            count: 1
        }
    ]);

    const handleReactionAdd = (emoji: string) => {
        const found = reactions.find((reaction) => reaction.emoji === emoji);

        if(!found) {
            setReactions([
                ...reactions,
                {
                    isOwn: true,
                    count: 1,
                    emoji
                }
            ]);

            return;
        }

        setReactions(reactions.map((reaction) => {
            if(reaction.emoji !== emoji) {
                return reaction;
            }

            return {
                ...reaction,
                count: reaction.count + (reaction.isOwn ? -1 : 1),
                isOwn: !reaction.isOwn
            };
        }).filter((reaction) => reaction.count > 0));
    };

    return <Bubble
        onReactionAdd={handleReactionAdd}
        reactions={reactions}
    >
        {t("bubblePage.reactionsAddDemo")}
    </Bubble>;
};

const BubblePage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        isReacted,
        setIsReacted
    ] = useState(false);

    const [
        reply,
        setReply
    ] = useState<string | undefined>(undefined);

    const [
        streamStep,
        setStreamStep
    ] = useState(STREAM_DONE);

    const words = t("bubblePage.streamSample").split(" ");

    const isStreaming = streamStep > 0 && streamStep < words.length;

    useEffect(() => {
        if(streamStep >= words.length) {
            return;
        }

        const timer = window.setTimeout(() => setStreamStep(streamStep + 1), streamStep === 0 ? TYPING_DELAY : WORD_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        words.length,
        streamStep
    ]);

    const handleReactionsPress = () => setIsReacted(!isReacted);

    const handleReplay = () => setStreamStep(0);

    const handleYes = () => setReply(t("bubblePage.replyYes"));

    const handleLater = () => setReply(t("bubblePage.replyLater"));

    const overviewReactions: Array<IBubbleReaction> = [
        {
            count: isReacted ? 3 : 2,
            isOwn: isReacted,
            emoji: "👍"
        }
    ];

    return <Article>
        <ComponentHeader
            lead={t("bubblePage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("bubblePage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("bubblePage.overviewAlternatives")}
            </Paragraph>
            <Preview
                caption={t("bubblePage.overviewCaption")}
                isStacked={true}
            >
                <Bubble
                    onReactionsPress={handleReactionsPress}
                    reactions={overviewReactions}
                >
                    {t("sample.message")}
                </Bubble>
                <Bubble
                    variant="user"
                >
                    {t("sample.reply")}
                </Bubble>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.variants")}
            id="variants"
        >
            <Paragraph>
                {t("bubblePage.variantsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Bubble>
                    {t("sample.message")}
                </Bubble>
                <Bubble
                    variant="user"
                >
                    {t("sample.reply")}
                </Bubble>
                <Bubble
                    variant="plain"
                >
                    {t("bubblePage.plainSample")}
                </Bubble>
                <Bubble
                    variant="system"
                >
                    {t("bubblePage.systemSample")}
                </Bubble>
                <Bubble
                    variant="danger"
                >
                    {t("bubblePage.dangerSample")}
                </Bubble>
            </Preview>
            <Paragraph>
                {t("bubblePage.groupText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Bubble.Group>
                    <Bubble>
                        {t("bubblePage.groupOne")}
                    </Bubble>
                    <Bubble>
                        {t("bubblePage.groupTwo")}
                    </Bubble>
                    <Bubble>
                        {t("bubblePage.groupThree")}
                    </Bubble>
                </Bubble.Group>
                <Bubble.Group
                    align="end"
                >
                    <Bubble
                        variant="user"
                    >
                        {t("sample.reply")}
                    </Bubble>
                    <Bubble
                        variant="user"
                    >
                        {t("bubblePage.userFollowUp")}
                    </Bubble>
                </Bubble.Group>
            </Preview>
            <CodeBlock
                code={GROUP_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("bubblePage.sizesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                {
                    SIZES.map((size) => <Bubble
                        size={size}
                        key={size}
                    >
                        {size}
                    </Bubble>)
                }
            </Preview>
        </Section>
        <Section
            title={t("bubblePage.reactionsTitle")}
            id="reactions"
        >
            <Paragraph>
                {t("bubblePage.reactionsText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Bubble
                    reactions={READ_ONLY_REACTIONS}
                >
                    {t("bubblePage.reactionsReadOnly")}
                </Bubble>
                <Bubble
                    reactionsTitle={t("bubblePage.reactionsLabel")}
                    onReactionsPress={() => undefined}
                    reactions={MANY_REACTIONS}
                >
                    {t("bubblePage.reactionsPressable")}
                </Bubble>
                <Bubble
                    onReactionsPress={() => undefined}
                    reactions={OWN_REACTIONS}
                >
                    {t("bubblePage.reactionsOwn")}
                </Bubble>
                <Bubble
                    onReactionsPress={() => undefined}
                    reactions={TOP_REACTIONS}
                    reactionsSide="top"
                >
                    {t("bubblePage.reactionsTop")}
                </Bubble>
            </Preview>
            <Paragraph>
                {t("bubblePage.reactionsAddText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <ReactionAddDemo />
                <Bubble
                    onReactionAdd={() => undefined}
                    variant="user"
                >
                    {t("bubblePage.reactionsAddDemo")}
                </Bubble>
            </Preview>
        </Section>
        <Section
            title={t("bubblePage.contentTitle")}
            id="content"
        >
            <Paragraph>
                {t("bubblePage.contentText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Bubble
                    variant="user"
                >
                    {t("bubblePage.longSample")}
                </Bubble>
            </Preview>
            <Paragraph>
                {t("bubblePage.markdownText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <Bubble
                    contentType="markdown"
                    variant="plain"
                >
                    {t("bubblePage.markdownSample")}
                </Bubble>
            </Preview>
            <Paragraph>
                {t("bubblePage.streamText")}
            </Paragraph>
            <Preview
                caption={t("bubblePage.streamCaption")}
                isStacked={true}
            >
                <Bubble
                    isStreaming={isStreaming}
                >
                    {
                        streamStep === 0 ?
                            <Bubble.Typing />
                        :
                            words.slice(0, streamStep).join(" ")
                    }
                </Bubble>
                <Button
                    isDisabled={streamStep < words.length}
                    title={t("bubblePage.replay")}
                    onPress={handleReplay}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
            </Preview>
            <CodeBlock
                code={STREAM_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("bubblePage.interactionTitle")}
            id="interaction"
        >
            <Paragraph>
                {t("bubblePage.interactionText")}
            </Paragraph>
            <Preview
                caption={reply ? t("bubblePage.chosen", [
                    reply
                ]) : t("bubblePage.chooseHint")}
                isStacked={true}
            >
                <Bubble
                    onPress={handleYes}
                    variant="user"
                >
                    {t("bubblePage.replyYes")}
                </Bubble>
                <Bubble
                    onPress={handleLater}
                    variant="user"
                >
                    {t("bubblePage.replyLater")}
                </Bubble>
                <Bubble
                    onPress={() => undefined}
                    isDisabled={true}
                    variant="user"
                >
                    {t("bubblePage.replyDisabled")}
                </Bubble>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("bubblePage.a11yBusy"),
                    t("bubblePage.a11yPressable"),
                    t("bubblePage.a11yReactions"),
                    t("bubblePage.a11yTyping"),
                    t("bubblePage.a11yCollapse"),
                    t("bubblePage.a11yGroup"),
                    t("bubblePage.a11yDirection"),
                    t("bubblePage.a11yParts")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        type: "\"user\" | \"assistant\" | \"plain\" | \"system\" | \"danger\"",
                        description: t("bubblePage.propVariant"),
                        defaultValue: "\"assistant\"",
                        name: "variant"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\"",
                        description: t("bubblePage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("bubblePage.propChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        description: t("bubblePage.propContentType"),
                        type: "\"text\" | \"markdown\"",
                        defaultValue: "\"text\"",
                        name: "contentType"
                    },
                    {
                        description: t("bubblePage.propOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("bubblePage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("bubblePage.propIsStreaming"),
                        defaultValue: "false",
                        name: "isStreaming",
                        type: "boolean"
                    },
                    {
                        description: t("bubblePage.propIsCollapsible"),
                        name: "isCollapsible",
                        defaultValue: "true",
                        type: "boolean"
                    },
                    {
                        description: t("bubblePage.propIsDefaultExpanded"),
                        name: "isDefaultExpanded",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        type: "Array<{ emoji: string; count: number; isOwn?: boolean }>",
                        description: t("bubblePage.propReactions"),
                        defaultValue: "[]",
                        name: "reactions"
                    },
                    {
                        description: t("bubblePage.propReactionsSide"),
                        type: "\"top\" | \"bottom\"",
                        defaultValue: "\"bottom\"",
                        name: "reactionsSide"
                    },
                    {
                        description: t("bubblePage.propOnReactionsPress"),
                        name: "onReactionsPress",
                        type: "() => void"
                    },
                    {
                        description: t("bubblePage.propOnReactionAdd"),
                        type: "(emoji: string) => void",
                        name: "onReactionAdd"
                    },
                    {
                        defaultValue: "[\"👍\", \"❤️\", \"😂\", \"🎉\", \"👀\", \"🙏\"]",
                        description: t("bubblePage.propQuickReactions"),
                        name: "quickReactions",
                        type: "Array<string>"
                    },
                    {
                        description: t("bubblePage.propReactionsTitle"),
                        name: "reactionsTitle",
                        type: "string"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("bubblePage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("bubblePage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("bubblePage.propsNote")}
            </Paragraph>
            <Paragraph>
                <InlineCode>
                    Bubble.Group
                </InlineCode>
                {" "}
                {t("bubblePage.groupPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("bubblePage.groupAlign"),
                        type: "\"start\" | \"end\"",
                        defaultValue: "\"start\"",
                        name: "align"
                    },
                    {
                        description: t("bubblePage.groupChildren"),
                        type: "ReactNode",
                        name: "children"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("bubblePage.groupCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Bubble.Typing
                </InlineCode>
                {" "}
                {t("bubblePage.typingPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("bubblePage.typingTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("bubblePage.typingCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("bubblePage.typingCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph>
                <InlineCode>
                    Bubble.Reactions
                </InlineCode>
                {" "}
                {t("bubblePage.reactionsPropsText")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        type: "Array<{ emoji: string; count: number; isOwn?: boolean }>",
                        description: t("bubblePage.partReactions"),
                        name: "reactions"
                    },
                    {
                        description: t("bubblePage.partSide"),
                        type: "\"top\" | \"bottom\"",
                        name: "side"
                    },
                    {
                        description: t("bubblePage.partAlign"),
                        type: "\"start\" | \"end\"",
                        name: "align"
                    },
                    {
                        description: t("bubblePage.partOnPress"),
                        type: "() => void",
                        name: "onPress"
                    },
                    {
                        description: t("bubblePage.propOnReactionAdd"),
                        type: "(emoji: string) => void",
                        name: "onReactionAdd"
                    },
                    {
                        description: t("bubblePage.propQuickReactions"),
                        name: "quickReactions",
                        type: "Array<string>"
                    },
                    {
                        description: t("bubblePage.partTitle"),
                        type: "string",
                        name: "title"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("bubblePage.partsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default BubblePage;
